import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import useInfiniteScroll from 'hooks/useInfiniteScroll/useInfiniteScroll';
import { TWhatsNewContent } from 'lendingApi/whatsNew';
import { getPathname } from 'utils/common/common';
import { PATH_NAMES } from 'utils/routes/pathNames';
import {
  getWhatsNewContent,
  markAllWhatsNewContentAsRead,
} from './useWhatsNew.api';

export const useWhatsNew = (
  containerRef: React.RefObject<HTMLDivElement>,
  introducerId: string,
  fetchDataOnly = false,
) => {
  const queryClient = useQueryClient();
  const [hasMarkedRead, setHasMarkedRead] = useState(false);
  const router = useRouter();

  const {
    data,
    hasNextPage,
    isLoading,
    isLoadingMore,
    isError,
    error,
    isEmpty,
  } = useInfiniteScroll<TWhatsNewContent>({
    queryKey: ['whatsNewContent'],
    apiFunction: (page) => getWhatsNewContent(introducerId as string, page),
    containerRef,
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => markAllWhatsNewContentAsRead(introducerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsNewContent'] });
    },
  });

  const numberUnread = (data || []).filter((item) => !item.is_read).length;

  useEffect(() => {
    if (
      !fetchDataOnly &&
      numberUnread > 0 &&
      !hasMarkedRead &&
      router.pathname === getPathname(PATH_NAMES.whatsNew())
    ) {
      markAllAsReadMutation.mutate();
      setHasMarkedRead(true); // To prevent infinite requests if the operation fails
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberUnread, hasMarkedRead, router.pathname]);

  return {
    data: data || [],
    isLoading,
    isLoadingMore,
    error,
    isError,
    hasMoreContent: hasNextPage,
    numberUnread,
    hasData: !isEmpty && !isError && data !== null,
    isEmpty,
  };
};
